import { useState, useRef, useEffect, useCallback, useMemo, memo } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Modal,
  KeyboardAvoidingView,
  Platform,
  ScrollView,
  ActivityIndicator,
  Linking,
} from "react-native";
import * as ImagePicker from "expo-image-picker";
import { Ionicons } from "@expo/vector-icons";

import { useAuth } from "../../context/AuthContext";
import { sendMessage, subscribeToMessages } from "../../services/communityService";
import EmbedPreview from "./EmbedPreview";

// ─── Palette ──────────────────────────────────────────────────────────────────

const BRAND       = "#A259FF";
const TEXT_PRI    = "rgba(255,255,255,0.90)";
const TEXT_SEC    = "rgba(255,255,255,0.60)";
const TEXT_TER    = "rgba(255,255,255,0.35)";
const BORDER      = "rgba(255,255,255,0.08)";
const CARD_BG     = "rgba(255,255,255,0.04)";
const INPUT_BG    = "rgba(255,255,255,0.07)";

// ─── Helpers ─────────────────────────────────────────────────────────────────

const AVATAR_COLORS = [
  "#A259FF", "#4ade80", "#f87171", "#fbbf24",
  "#60a5fa", "#f472b6", "#34d399", "#fb923c",
];

function avatarColor(str = "") {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
}

function initials(str = "") {
  const parts = str.replace(/\d+$/, "").match(/[A-Z][a-z]*/g) || [str];
  return parts.slice(0, 2).map((p) => p[0]).join("").toUpperCase() || str.slice(0, 2).toUpperCase();
}

function formatTime(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function formatDate(createdAt) {
  if (!createdAt) return "";
  const date = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  const now = new Date();
  if (date.toDateString() === now.toDateString()) return "Today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) return "Yesterday";
  return date.toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
}

function dayKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

function minuteKey(createdAt) {
  if (!createdAt) return "";
  const d = createdAt.toDate ? createdAt.toDate() : new Date(createdAt);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}-${d.getHours()}-${d.getMinutes()}`;
}

// ─── URL helpers ─────────────────────────────────────────────────────────────

const URL_RE = /https?:\/\/[^\s<>"]+/g;

function parseTextSegments(text) {
  const segments = [];
  let lastIndex = 0;
  for (const match of text.matchAll(URL_RE)) {
    if (match.index > lastIndex)
      segments.push({ kind: "text", value: text.slice(lastIndex, match.index) });
    segments.push({ kind: "url", value: match[0] });
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length)
    segments.push({ kind: "text", value: text.slice(lastIndex) });
  return segments;
}

// ─── Day separator ────────────────────────────────────────────────────────────

const DaySeparator = memo(function DaySeparator({ createdAt }) {
  return (
    <View style={styles.daySep}>
      <View style={styles.daySepLine} />
      <Text style={styles.daySepText}>{formatDate(createdAt)}</Text>
      <View style={styles.daySepLine} />
    </View>
  );
});

// ─── Message row ─────────────────────────────────────────────────────────────

const MessageRow = memo(function MessageRow({ msg, showHeader, showTimestamp, isOwn }) {
  const displayName = msg.username || msg.displayName || "Unknown";
  const color = avatarColor(displayName);
  const abbr = initials(displayName);

  const segments = useMemo(() => parseTextSegments(msg.text ?? ""), [msg.text]);
  const embedUrl = useMemo(
    () => segments.find((s) => s.kind === "url")?.value ?? msg.videoUrl ?? null,
    [segments, msg.videoUrl],
  );

  return (
    <View style={[styles.msgRow, showHeader && styles.msgRowFirst]}>
      {/* Left gutter: avatar or time stamp */}
      {showHeader ? (
        <View style={[styles.msgAvatar, { backgroundColor: color }]}>
          <Text style={styles.msgAvatarText}>{abbr}</Text>
        </View>
      ) : showTimestamp ? (
        <Text style={styles.msgGutterTime}>{formatTime(msg.createdAt)}</Text>
      ) : (
        <View style={styles.msgGutter} />
      )}

      {/* Content */}
      <View style={styles.msgContent}>
        {showHeader && (
          <View style={styles.msgHeader}>
            <Text style={[styles.msgUsername, { color }]}>{displayName}</Text>
            <Text style={styles.msgTime}>{formatTime(msg.createdAt)}</Text>
          </View>
        )}
        <Text style={styles.msgText}>
          {segments.map((seg, i) =>
            seg.kind === "url" ? (
              <Text
                key={i}
                style={styles.msgLink}
                onPress={() => Linking.openURL(seg.value).catch(() => {})}
              >
                {seg.value}
              </Text>
            ) : (
              <Text key={i}>{seg.value}</Text>
            ),
          )}
        </Text>
        {!!embedUrl && <EmbedPreview url={embedUrl} />}
      </View>
    </View>
  );
});

// ─── Main component ───────────────────────────────────────────────────────────

export default function ChannelContent({ communityId, categoryId, channelId, channelName }) {
  const { user } = useAuth();
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [sendNotice, setSendNotice] = useState(null);
  const [videoModalVisible, setVideoModalVisible] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const scrollRef = useRef(null);

  const resolvedName = channelName ?? channelId ?? "";
  const canSend = !!(communityId && categoryId && channelId);

  useEffect(() => {
    if (!communityId || !categoryId || !channelId) {
      setMessages([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = subscribeToMessages(communityId, categoryId, channelId, (msgs) => {
      setMessages(msgs);
      setLoading(false);
    });
    return unsub;
  }, [communityId, categoryId, channelId]);

  useEffect(() => {
    setTimeout(() => scrollRef.current?.scrollToEnd({ animated: false }), 50);
  }, [messages]);

  const handlePickImage = useCallback(async () => {
    const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
    if (status !== "granted") return;
    await ImagePicker.launchImageLibraryAsync({
      mediaTypes: ["images"],
      allowsMultipleSelection: true,
      quality: 0.85,
    });
  }, []);

  const handleAttachVideoUrl = useCallback(() => {
    setVideoUrl("");
    setVideoModalVisible(true);
  }, []);

  const handleConfirmVideoUrl = useCallback(() => {
    if (videoUrl.trim())
      setMessage((prev) => (prev ? `${prev} ${videoUrl.trim()}` : videoUrl.trim()));
    setVideoModalVisible(false);
  }, [videoUrl]);

  const handleSend = useCallback(async () => {
    if (!message.trim() || !canSend || sending) return;
    const text = message.trim();
    setSendError(null);
    setSendNotice(null);
    setMessage("");
    setSending(true);

    const result = await sendMessage(communityId, categoryId, channelId, {
      uid: user?.uid ?? "anonymous",
      username: user?.username ?? (user?.email ? user.email.split("@")[0] : null) ?? user?.displayName ?? null,
      displayName: user?.displayName ?? user?.username ?? null,
      photoURL: user?.photoURL ?? user?.profilePhoto ?? null,
      text,
    });

    if (result.blocked) {
      setMessage(text);
      setSendError("Message blocked: violates community guidelines.");
    } else if (result.error) {
      setSendError("Failed to send. Please try again.");
    } else if (result.flagged) {
      setSendNotice("Message sent and queued for review.");
    }
    setSending(false);
  }, [message, communityId, categoryId, channelId, user, canSend, sending]);

  // Build message list with day separators
  const renderedItems = useMemo(() => {
    if (!messages.length) return [];
    const items = [];
    let lastDay = null;
    messages.forEach((msg, idx) => {
      const day = dayKey(msg.createdAt);
      if (day !== lastDay) {
        items.push({ type: "day", key: `day-${day}`, createdAt: msg.createdAt });
        lastDay = day;
      }
      const prev = messages[idx - 1];
      const newDay = idx === 0 || dayKey(prev?.createdAt) !== day;
      const showHeader = newDay || idx === 0 || prev?.uid !== msg.uid;
      const showTimestamp = !showHeader && minuteKey(prev?.createdAt) !== minuteKey(msg.createdAt);
      items.push({ type: "msg", key: msg.id, msg, showHeader, showTimestamp, isOwn: msg.uid === user?.uid });
    });
    return items;
  }, [messages, user?.uid]);

  const canSubmit = message.trim() && canSend && !sending;

  return (
    <View style={styles.root}>
      {/* Channel header */}
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <Text style={styles.headerHash}>#</Text>
          <Text style={styles.headerName} numberOfLines={1}>{resolvedName}</Text>
        </View>
        <View style={styles.headerActions}>
          <TouchableOpacity style={styles.headerBtn} hitSlop={8}>
            <Ionicons name="search-outline" size={18} color={TEXT_TER} />
          </TouchableOpacity>
          <TouchableOpacity style={styles.headerBtn} hitSlop={8}>
            <Ionicons name="ellipsis-horizontal" size={18} color={TEXT_TER} />
          </TouchableOpacity>
        </View>
      </View>

      {/* Messages */}
      {loading ? (
        <View style={styles.centered}>
          <ActivityIndicator color={BRAND} size="small" />
        </View>
      ) : (
        <ScrollView
          ref={scrollRef}
          style={styles.msgList}
          contentContainerStyle={styles.msgListInner}
          showsVerticalScrollIndicator={false}
          onContentSizeChange={() => scrollRef.current?.scrollToEnd({ animated: false })}
        >
          {renderedItems.length === 0 ? (
            <View style={styles.emptyCentered}>
              <View style={styles.emptyIcon}>
                <Text style={styles.emptyHash}>#</Text>
              </View>
              <Text style={styles.emptyTitle}>Welcome to #{resolvedName}</Text>
              <Text style={styles.emptyBody}>This is the start of the channel. Be the first to say something!</Text>
            </View>
          ) : (
            renderedItems.map((item) =>
              item.type === "day" ? (
                <DaySeparator key={item.key} createdAt={item.createdAt} />
              ) : (
                <MessageRow
                  key={item.key}
                  msg={item.msg}
                  showHeader={item.showHeader}
                  showTimestamp={item.showTimestamp}
                  isOwn={item.isOwn}
                />
              ),
            )
          )}
        </ScrollView>
      )}

      {/* Notices */}
      {!!sendError && (
        <View style={styles.noticeBar}>
          <Ionicons name="alert-circle-outline" size={13} color="#f87171" style={{ marginRight: 6 }} />
          <Text style={styles.noticeTextError}>{sendError}</Text>
        </View>
      )}
      {!!sendNotice && (
        <View style={styles.noticeBar}>
          <Ionicons name="checkmark-circle-outline" size={13} color="#4ade80" style={{ marginRight: 6 }} />
          <Text style={styles.noticeTextOk}>{sendNotice}</Text>
        </View>
      )}

      {/* Chat input bar */}
      <KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined}>
        <View style={styles.chatBar}>
          {/* Attach actions */}
          <TouchableOpacity style={styles.attachBtn} onPress={handlePickImage} hitSlop={8}>
            <Ionicons name="add-circle-outline" size={22} color={TEXT_TER} />
          </TouchableOpacity>

          {/* Input area */}
          <View style={styles.inputWrap}>
            <TextInput
              style={styles.textInput}
              value={message}
              onChangeText={(val) => {
                setMessage(val);
                if (sendError) setSendError(null);
                if (sendNotice) setSendNotice(null);
              }}
              placeholder={`Message #${resolvedName}`}
              placeholderTextColor={TEXT_TER}
              multiline
              maxLength={2000}
              onSubmitEditing={Platform.OS === "web" ? handleSend : undefined}
            />
            {/* Inline action row inside input */}
            <View style={styles.inputActions}>
              <TouchableOpacity onPress={handleAttachVideoUrl} hitSlop={8} style={styles.inputActionBtn}>
                <Ionicons name="play-circle-outline" size={18} color={TEXT_TER} />
              </TouchableOpacity>
            </View>
          </View>

          {/* Send button */}
          <TouchableOpacity
            onPress={handleSend}
            style={[styles.sendBtn, canSubmit && styles.sendBtnActive]}
            disabled={!canSubmit}
            hitSlop={4}
          >
            {sending ? (
              <ActivityIndicator size="small" color={canSubmit ? "#fff" : TEXT_TER} />
            ) : (
              <Ionicons name="send" size={16} color={canSubmit ? "#fff" : TEXT_TER} />
            )}
          </TouchableOpacity>
        </View>
      </KeyboardAvoidingView>

      {/* Video URL modal */}
      <Modal
        visible={videoModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setVideoModalVisible(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setVideoModalVisible(false)}
        >
          <View style={styles.modalCard} onStartShouldSetResponder={() => true}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Embed Video URL</Text>
              <TouchableOpacity onPress={() => setVideoModalVisible(false)} hitSlop={8}>
                <Ionicons name="close" size={18} color={TEXT_SEC} />
              </TouchableOpacity>
            </View>
            <Text style={styles.modalHint}>Paste a YouTube, Twitch, or other video link</Text>
            <TextInput
              style={styles.modalInput}
              value={videoUrl}
              onChangeText={setVideoUrl}
              placeholder="https://..."
              placeholderTextColor={TEXT_TER}
              autoFocus
              autoCapitalize="none"
              keyboardType="url"
            />
            <View style={styles.modalFooter}>
              <TouchableOpacity onPress={() => setVideoModalVisible(false)} style={styles.modalBtnSecondary}>
                <Text style={styles.modalBtnSecondaryText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                onPress={handleConfirmVideoUrl}
                style={[styles.modalBtnPrimary, !videoUrl.trim() && { opacity: 0.4 }]}
                disabled={!videoUrl.trim()}
              >
                <Text style={styles.modalBtnPrimaryText}>Attach</Text>
              </TouchableOpacity>
            </View>
          </View>
        </TouchableOpacity>
      </Modal>
    </View>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: CARD_BG,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: BORDER,
    overflow: "hidden",
    flexDirection: "column",
  },

  // ── Header ────────────────────────────────────────────────────────
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    paddingVertical: 13,
    borderBottomWidth: 1,
    borderBottomColor: BORDER,
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  headerLeft: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  headerHash: {
    fontSize: 20,
    fontWeight: "700",
    color: TEXT_TER,
    lineHeight: 22,
  },
  headerName: {
    fontSize: 15,
    fontWeight: "700",
    color: TEXT_PRI,
  },
  headerActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  headerBtn: {
    width: 32,
    height: 32,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 6,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },

  // ── Messages ──────────────────────────────────────────────────────
  msgList: {
    flex: 1,
  },
  msgListInner: {
    paddingHorizontal: 16,
    paddingTop: 8,
    paddingBottom: 8,
  },

  centered: {
    flex: 1,
    alignItems: "center",
    justifyContent: "center",
  },

  // Empty state
  emptyCentered: {
    flex: 1,
    alignItems: "center",
    paddingTop: 48,
    paddingHorizontal: 24,
  },
  emptyIcon: {
    width: 64,
    height: 64,
    borderRadius: 16,
    backgroundColor: "rgba(162,89,255,0.15)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    borderWidth: 1,
    borderColor: "rgba(162,89,255,0.25)",
  },
  emptyHash: {
    fontSize: 32,
    fontWeight: "800",
    color: BRAND,
  },
  emptyTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: TEXT_PRI,
    marginBottom: 8,
    textAlign: "center",
  },
  emptyBody: {
    fontSize: 14,
    color: TEXT_TER,
    textAlign: "center",
    lineHeight: 20,
  },

  // Day separator
  daySep: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 16,
    gap: 10,
  },
  daySepLine: {
    flex: 1,
    height: 1,
    backgroundColor: BORDER,
  },
  daySepText: {
    fontSize: 11,
    fontWeight: "600",
    color: TEXT_TER,
    letterSpacing: 0.3,
  },

  // Message row
  msgRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    paddingVertical: 1,
  },
  msgRowFirst: {
    marginTop: 10,
  },
  msgAvatar: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: "center",
    justifyContent: "center",
    marginRight: 10,
    flexShrink: 0,
  },
  msgAvatarText: {
    color: "#fff",
    fontSize: 12,
    fontWeight: "700",
  },
  msgGutter: {
    width: 36,
    marginRight: 10,
    flexShrink: 0,
  },
  msgGutterTime: {
    width: 36,
    marginRight: 10,
    flexShrink: 0,
    fontSize: 9,
    color: TEXT_TER,
    textAlign: "center",
    alignSelf: "center",
    lineHeight: 14,
  },
  msgContent: {
    flex: 1,
  },
  msgHeader: {
    flexDirection: "row",
    alignItems: "baseline",
    gap: 8,
    marginBottom: 2,
  },
  msgUsername: {
    fontSize: 14,
    fontWeight: "600",
  },
  msgTime: {
    fontSize: 10,
    color: TEXT_TER,
  },
  msgText: {
    fontSize: 14,
    color: TEXT_PRI,
    lineHeight: 20,
  },
  msgLink: {
    color: BRAND,
    textDecorationLine: "underline",
  },

  // ── Notice bar ────────────────────────────────────────────────────
  noticeBar: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 16,
    paddingVertical: 6,
    backgroundColor: "rgba(0,0,0,0.3)",
  },
  noticeTextError: {
    fontSize: 12,
    color: "#f87171",
  },
  noticeTextOk: {
    fontSize: 12,
    color: "#4ade80",
  },

  // ── Chat bar ──────────────────────────────────────────────────────
  chatBar: {
    flexDirection: "row",
    alignItems: "flex-end",
    gap: 8,
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderTopWidth: 1,
    borderTopColor: BORDER,
    backgroundColor: "rgba(0,0,0,0.2)",
  },
  attachBtn: {
    width: 36,
    height: 36,
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    marginBottom: 1,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  inputWrap: {
    flex: 1,
    backgroundColor: INPUT_BG,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    flexDirection: "row",
    alignItems: "flex-end",
    paddingLeft: 14,
    paddingRight: 8,
    paddingVertical: 2,
    minHeight: 40,
  },
  textInput: {
    flex: 1,
    fontSize: 14,
    color: TEXT_PRI,
    paddingVertical: 9,
    maxHeight: 120,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  inputActions: {
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 8,
    gap: 2,
  },
  inputActionBtn: {
    padding: 4,
    borderRadius: 6,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  sendBtn: {
    width: 36,
    height: 36,
    borderRadius: 10,
    backgroundColor: "rgba(255,255,255,0.08)",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 1,
    ...(Platform.OS === "web" ? { cursor: "pointer" } : {}),
  },
  sendBtnActive: {
    backgroundColor: BRAND,
  },

  // ── Video modal ───────────────────────────────────────────────────
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0,0,0,0.7)",
    justifyContent: "center",
    alignItems: "center",
    padding: 24,
  },
  modalCard: {
    width: "100%",
    maxWidth: 420,
    backgroundColor: "#1a1a1a",
    borderRadius: 14,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.1)",
    padding: 20,
  },
  modalHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 12,
  },
  modalTitle: {
    fontSize: 16,
    fontWeight: "700",
    color: TEXT_PRI,
  },
  modalHint: {
    fontSize: 13,
    color: TEXT_SEC,
    marginBottom: 12,
    lineHeight: 18,
  },
  modalInput: {
    backgroundColor: INPUT_BG,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
    paddingHorizontal: 12,
    paddingVertical: 10,
    color: TEXT_PRI,
    fontSize: 14,
    ...(Platform.OS === "web" ? { outlineStyle: "none" } : {}),
  },
  modalFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    gap: 10,
    marginTop: 16,
  },
  modalBtnSecondary: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "rgba(255,255,255,0.12)",
  },
  modalBtnSecondaryText: {
    color: TEXT_SEC,
    fontSize: 14,
    fontWeight: "500",
  },
  modalBtnPrimary: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: BRAND,
  },
  modalBtnPrimaryText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
});
